import { Drawer, FocusTrap, Box, Center } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import styles from './SortFilterMobile.module.scss';
import { ReactComponent as IconSortMobile } from '../img/filters/iconSortMobile.svg';
import { ReactComponent as IconSortMobileBlue } from '../img/filters/iconSortMobileBlue.svg';
import { ReactComponent as IconCloseBlue } from '../img/filters/iconCloseBlue.svg';
import { ReactComponent as IconFilterSettings } from '../img/filters/iconFilterSettingts.svg';
import { ReactComponent as IconFilterSettingsActive } from '../img/filters/iconFilterSettingtsActive.svg';
import { ReactComponent as IconCloseActive } from '../img/filters/iconCloseFilter.svg';
import { ReactComponent as IconChevronLeft } from '../img/iconChewronLeft.svg';
import { ReactComponent as IconCalendar } from '../img/filters/iconCalendar.svg';
import classNames from 'classnames';
import { EmployedsFilterCheckbox } from 'components/EmployedsFilters/EmployedsFilterCheckbox';
import { EmployedsMobileFilterDrawer } from 'components/EmployedsFiltersMobile/EmployedsMobileFilterDrawer';
import { EmployedsMobileFilterGroup } from 'components/EmployedsFiltersMobile/EmployedsMobileFilterGroup';
import { FilterButton } from 'components/OrdersFilters/FilterButton';
import { useState, useEffect, useCallback } from 'react';
import type { CategoryItem, EmployedsFilterParams } from '../Filters/Filters';
import { useGetCategoryEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { ReactComponent as ChevronDownIcon } from '../img/iconChewron2.svg';
import { ReactComponent as SearchIcon } from '../img/iconSearch2.svg';
import iconFilter from '../img/filters/iconFilter.svg';
import iconFilterActive from '../img/filters/iconFilterActive.svg';
import employedsFilterStyles from '../Filters/Filters.module.scss';

interface FiltersType {
  id: number;
  keyword: string;
  label: string;
}

interface MobileTabsSortType {
  value: string;
  label: string;
}

function formatDateForAPI(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export const MobileMenu = ({
  mobileTabsSort,
  handleTabClickSort,
  activeSortName,
  dataFiltersMobile,
  initialFilters = {},
  onApplyFilters,
  onResetFilters,
  mobileTabs,
  handleTabClick,
  activeTabName,
  toggleSearchOpen,
  filtersOnly = false,
  filtersOnlyIconOnly = false,
  showFilters = false,
}: {
  mobileTabsSort: MobileTabsSortType[];
  handleTabClickSort: (val: string) => void;
  activeSortName: string;
  localStorageKey?: string;
  setActiveSort: (tab: string) => void;
  dataFiltersMobile?: {
    genders: FiltersType[];
    issues: FiltersType[];
    nationalities: FiltersType[];
    categories: FiltersType[];
    contracts: FiltersType[];
  };
  showFilters?: boolean;
  initialFilters?: EmployedsFilterParams;
  onApplyFilters?: (filters: EmployedsFilterParams) => void;
  onResetFilters?: () => void;
  mobileTabs: MobileTabsSortType[];
  handleTabClick: (val: string) => void;
  activeTabName: string;
  toggleSearchOpen: () => void;
  filtersOnly?: boolean;
  filtersOnlyIconOnly?: boolean;
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [openedBox, { open: openBox, close: closeBox }] = useDisclosure(false);
  const [openedFilter, { open: openFilter, close: closeFilter }] = useDisclosure(false);
  const [openedDate, { open: openDate, close: closeDate }] = useDisclosure(false);
  const [rangeValue, setRangeValue] = useState<[Date | null, Date | null]>([null, null]);

  const [nationalityKeywords, setNationalityKeywords] = useState<string[]>(
    initialFilters.nationalities ?? [],
  );
  const [categoriesKeywords, setCategoriesKeywords] = useState<CategoryItem[]>(
    initialFilters.categories ?? [],
  );
  const [genderKeywords, setGenderKeywords] = useState<string[]>(
    initialFilters.genders ?? [],
  );
  const [contractKeywords, setContractKeywords] = useState<string[]>(
    initialFilters.contracts ?? [],
  );
  const [issueKeywords, setIssueKeywords] = useState<string[]>(
    initialFilters.issues ?? [],
  );
  const [dateAddStart, setDateAddStart] = useState<Date | null>(
    initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
  );
  const [dateAddEnd, setDateAddEnd] = useState<Date | null>(
    initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
  );

  useEffect(() => {
    setNationalityKeywords(initialFilters.nationalities ?? []);
    setCategoriesKeywords(initialFilters.categories ?? []);
    setGenderKeywords(initialFilters.genders ?? []);
    setIssueKeywords(initialFilters.issues ?? []);
    setContractKeywords(initialFilters.contracts ?? []);
    setDateAddStart(
      initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
    );
    setDateAddEnd(
      initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
    );
  }, [
    initialFilters.nationalities,
    initialFilters.categories,
    initialFilters.genders,
    initialFilters.issues,
    initialFilters.contracts,
    initialFilters.date_add?.start,
    initialFilters.date_add?.end,
  ]);

  const [queryParamsCategorys, setQueryParamsCategorys] = useState({
    page: 1,
    search: '',
  });

  const handleCategorysServerSearch = useCallback((search: string) => {
    setQueryParamsCategorys((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return { ...prev, page: 1, search };
    });
  }, []);

  const {
    data: categorysFiltersData,
    isLoading: isLoadingCategorys,
    isFetching: isFetchingCategorys,
  } = useGetCategoryEmployedsQuery(
    {
      page: queryParamsCategorys.page,
      ...(queryParamsCategorys.search ? { search: queryParamsCategorys.search } : {}),
    },
    { skip: !openedFilter },
  );

  const hasNextCategoryPage =
    !!categorysFiltersData?.meta &&
    categorysFiltersData.meta.current_page < categorysFiltersData.meta.last_page;

  const loadMoreCategories = useCallback(() => {
    if (isFetchingCategorys) return;
    if (!hasNextCategoryPage) return;
    setQueryParamsCategorys((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingCategorys, hasNextCategoryPage]);

  const [filterDrawerState, setFilterDrawerState] = useState<{
    opened: boolean;
    title: string;
    options: { id: string; label: string }[];
    selectedValues: string[];
    filterType: string;
  }>({
    opened: false,
    title: '',
    options: [],
    selectedValues: [],
    filterType: '',
  });

  const handleClearSort = () => {
    handleTabClickSort('default');
  };

  const transformNationalities = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.nationalities || []).map((national) => ({
      id: national.keyword,
      label: national.label,
    }));
  };

  const transformCategories = (): { id: string; label: string }[] => {
    return (categorysFiltersData?.data || []).map((c) => ({
      id: String(c.id),
      label: c.name || '',
    }));
  };

  const transformGenders = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.genders || []).map((g) => ({
      id: g.keyword,
      label: g.label,
    }));
  };

  const transformContracts = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.contracts || []).map((g) => ({
      id: g.keyword,
      label: g.label,
    }));
  };

  const transformIssues = (): { id: string; label: string }[] => {
    return (dataFiltersMobile?.issues || []).map((issue) => ({
      id: issue.keyword,
      label: issue.label,
    }));
  };

  const handleItemClick = (filterType: string, optionId: string) => {
    switch (filterType) {
      case 'nationalities': {
        if (nationalityKeywords.includes(optionId)) {
          setNationalityKeywords(nationalityKeywords.filter((k) => k !== optionId));
        } else {
          setNationalityKeywords([...nationalityKeywords, optionId]);
        }
        break;
      }
      case 'categories': {
        const isSelected = categoriesKeywords.some((item) => item.id === optionId);
        if (isSelected) {
          setCategoriesKeywords(categoriesKeywords.filter((k) => k.id !== optionId));
        } else {
          const newCategory = {
            id: optionId,
            name:
              transformCategories().find((opt) => opt.id === optionId)?.label || optionId,
          };
          setCategoriesKeywords([...categoriesKeywords, newCategory]);
        }
        break;
      }
      case 'genders': {
        if (genderKeywords.includes(optionId)) {
          setGenderKeywords(genderKeywords.filter((k) => k !== optionId));
        } else {
          setGenderKeywords([...genderKeywords, optionId]);
        }
        break;
      }
      case 'issues': {
        if (issueKeywords.includes(optionId)) {
          setIssueKeywords(issueKeywords.filter((k) => k !== optionId));
        } else {
          setIssueKeywords([...issueKeywords, optionId]);
        }
        break;
      }
    }
  };

  const handleItemRemove = (filterType: string, optionId: string) => {
    handleItemClick(filterType, optionId);
  };

  const openFilterDrawer = (filterType: string) => {
    let options: { id: string; label: string }[] = [];
    let selectedValues: string[] = [];
    let title = '';

    switch (filterType) {
      case 'nationalities':
        options = transformNationalities();
        selectedValues = nationalityKeywords;
        title = 'Гражданство';
        break;
      case 'categories':
        options = transformCategories();
        selectedValues = categoriesKeywords.map((c) => c.id);
        title = 'Категории исполнителей';
        break;
      case 'issues':
        options = transformIssues();
        selectedValues = issueKeywords;
        title = 'Проблема';
        break;
    }

    setFilterDrawerState({
      opened: true,
      title,
      options,
      selectedValues,
      filterType,
    });
  };

  const handleFilterDrawerChange = (values: string[]) => {
    switch (filterDrawerState.filterType) {
      case 'nationalities':
        setNationalityKeywords(values);
        break;
      case 'categories': {
        const categoryItems = values.map((id) => ({
          id,
          name:
            transformCategories().find((opt) => opt.id === id)?.label ||
            categoriesKeywords.find((c) => c.id === id)?.name ||
            id,
        }));
        setCategoriesKeywords(categoryItems);
        break;
      }
      case 'issues':
        setIssueKeywords(values);
        break;
    }
    setFilterDrawerState((prev) => ({ ...prev, selectedValues: values }));
  };

  const handleFilterDrawerApply = () => {
    setFilterDrawerState((prev) => ({ ...prev, opened: false }));
  };

  const handleFilterDrawerReset = () => {
    switch (filterDrawerState.filterType) {
      case 'nationalities':
        setNationalityKeywords([]);
        break;
      case 'categories':
        setCategoriesKeywords([]);
        break;
      case 'issues':
        setIssueKeywords([]);
        break;
    }
    setFilterDrawerState((prev) => ({
      ...prev,
      opened: false,
      selectedValues: [],
    }));
  };

  const openDatePicker = () => {
    setRangeValue([dateAddStart, dateAddEnd]);
    openDate();
  };

  const getAppliedFiltersCount = (): number => {
    let count = 0;
    if ((initialFilters.nationalities?.length ?? 0) > 0) count++;
    if ((initialFilters.categories?.length ?? 0) > 0) count++;
    if ((initialFilters.genders?.length ?? 0) > 0) count++;
    if ((initialFilters.issues?.length ?? 0) > 0) count++;
    if ((initialFilters.contracts?.length ?? 0) > 0) count++;
    if (initialFilters.date_add?.start != null || initialFilters.date_add?.end != null) {
      count++;
    }
    return count;
  };

  const activeFiltersCount = getAppliedFiltersCount();
  const hasFiltersApplied = activeFiltersCount > 0;
  const showFilterButtonText = !(filtersOnly && filtersOnlyIconOnly);

  const buildFiltersPayload = (): EmployedsFilterParams => {
    const start = dateAddStart;
    const end = dateAddEnd ?? dateAddStart;
    const date_add =
      start != null && end != null
        ? { start: formatDateForAPI(start), end: formatDateForAPI(end) }
        : undefined;
    return {
      genders: genderKeywords,
      issues: issueKeywords,
      nationalities: nationalityKeywords,
      categories: categoriesKeywords,
      contracts: contractKeywords,
      date_add,
    };
  };

  const handleApplyFilters = () => {
    onApplyFilters?.(buildFiltersPayload());
    closeFilter();
  };

  const handleResetAllFilters = () => {
    setNationalityKeywords([]);
    setCategoriesKeywords([]);
    setGenderKeywords([]);
    setIssueKeywords([]);
    setContractKeywords([]);
    setDateAddStart(null);
    setDateAddEnd(null);
    onResetFilters?.();
    closeFilter();
  };

  const formatDateRangeForDisplay = (start: Date | null, end: Date | null): string => {
    if (!start && !end) return '';
    if (start && !end) return formatDateForDisplay(start);
    if (!start && end) return formatDateForDisplay(end);
    if (start && end && start.getTime() === end.getTime()) {
      return formatDateForDisplay(start);
    }
    return start && end
      ? `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`
      : '';
  };

  return (
    <>
      <div
        className={classNames(
          styles.root,
          filtersOnly && styles.rootFiltersOnly,
          filtersOnly && styles.rootNoBottomMargin,
        )}>
        {!filtersOnly && (
          <>
            <div className={styles.search} onClick={toggleSearchOpen}>
              <SearchIcon />
            </div>
            <div className={styles.selector} onClick={openBox}>
              <span>
                {activeTabName === 'Готовы к сотрудничеству'
                  ? 'Готовы'
                  : activeTabName === 'Не готовы к сотрудничеству'
                  ? 'Не готовы'
                  : activeTabName === 'В стоп-листе'
                  ? 'Стоп-лист'
                  : activeTabName}
              </span>
              <ChevronDownIcon />
            </div>

            <div
              className={styles.selector}
              onClick={open}
              style={{
                background: activeSortName === 'default' ? '#FFFFFF' : '#E1E6FE',
              }}>
              {activeSortName === 'default' ? <IconSortMobile /> : <IconSortMobileBlue />}
              {activeSortName !== 'default' && (
                <IconCloseBlue
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleClearSort();
                  }}
                />
              )}
            </div>
          </>
        )}

        {filtersOnly ? (
          <button
            type='button'
            className={classNames(
              employedsFilterStyles.filterButton,
              hasFiltersApplied && employedsFilterStyles.active,
              filtersOnlyIconOnly && employedsFilterStyles.filterButtonIconOnly,
            )}
            onClick={openFilter}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
              {(!showFilterButtonText || !hasFiltersApplied) && (
                <img
                  src={hasFiltersApplied ? iconFilterActive : iconFilter}
                  alt='Фильтр'
                  className={employedsFilterStyles.filterIcon}
                />
              )}
              {showFilterButtonText && (
                <span
                  style={{
                    color: hasFiltersApplied ? '#002CFB' : undefined,
                  }}>
                  Фильтр
                </span>
              )}
              {hasFiltersApplied && activeFiltersCount > 1 ? (
                <div className={employedsFilterStyles.filterCountBadge}>
                  {activeFiltersCount}
                </div>
              ) : null}
              {hasFiltersApplied && (
                <IconCloseBlue
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleResetAllFilters();
                  }}
                />
              )}
            </div>
          </button>
        ) : (
          <>
            {activeFiltersCount === 0 && (
              <div className={styles.filter} onClick={openFilter}>
                <IconFilterSettings />
                <span>Фильтр</span>
              </div>
            )}
            {activeFiltersCount === 1 && (
              <div
                className={classNames(styles.filter, styles.filterActive)}
                onClick={openFilter}>
                <IconFilterSettingsActive />
                <span>Фильтр</span>
                <IconCloseActive
                  style={{ marginLeft: '4px' }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleResetAllFilters();
                  }}
                />
              </div>
            )}

            {activeFiltersCount >= 2 && (
              <div
                className={classNames(styles.filter, styles.filterActive)}
                onClick={openFilter}>
                <IconFilterSettingsActive />
                <span>Фильтр</span>
                <span className={styles.filterCount}>{activeFiltersCount}</span>
                <IconCloseActive
                  style={{ marginLeft: '4px' }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleResetAllFilters();
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {!filtersOnly && (
        <>
          <Drawer
            opened={openedBox}
            onClose={closeBox}
            position='bottom'
            size={217}
            zIndex={10000}
            withCloseButton={false}
            styles={{
              content: {
                borderRadius: '13px 13px 0px 0px',
                backgroundColor: '#FFF',
                padding: '12px 16px 32px 16px',
                boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
              },
              body: {
                padding: '0',
              },
            }}>
            {mobileTabs.map((tab, index) => {
              return (
                <div
                  key={tab.value}
                  className={`${styles.tab} ${
                    tab.label === activeTabName ? styles.tab_active : ''
                  } ${index === 0 ? styles.tab_first : ''} ${
                    index === mobileTabs.length - 1 ? styles.tab_last : ''
                  }`}
                  onClick={() => {
                    handleTabClick(tab.value);
                    closeBox();
                  }}>
                  {tab.label}
                </div>
              );
            })}
          </Drawer>

          <Drawer
            opened={opened}
            onClose={close}
            position='bottom'
            size={270}
            zIndex={10000}
            withCloseButton={false}
            styles={{
              content: {
                borderRadius: '13px 13px 0px 0px',
                backgroundColor: '#FFF',
                padding: '12px 16px 32px 16px',
                boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
              },
              body: {
                padding: '0',
              },
            }}>
            {mobileTabsSort.map((tab, index) => {
              return (
                <div
                  key={tab.value}
                  className={`${styles.tab} ${
                    tab.value === activeSortName ? styles.tab_active : ''
                  } ${index === 0 ? styles.tab_first : ''} ${
                    index === mobileTabsSort.length - 1 ? styles.tab_last : ''
                  }`}
                  onClick={() => {
                    handleTabClickSort(tab.value);
                    close();
                  }}>
                  {tab.label}
                </div>
              );
            })}
          </Drawer>
        </>
      )}

      <Drawer
        opened={openedFilter}
        onClose={closeFilter}
        styles={{
          content: {
            backgroundColor: '#FFF',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}
        position='bottom'
        size={'100%'}
        zIndex={10000}
        withCloseButton={false}>
        <FocusTrap.InitialFocus />
        <div>
          <div className={styles.header}>
            <div className={styles.filterIcon}>
              <IconChevronLeft onClick={closeFilter} />
            </div>
            <span className={styles.filterTitle}>Фильтр</span>
            <span
              className={styles.filterClear}
              onClick={() => {
                handleResetAllFilters();
                closeFilter();
              }}>
              Сбросить
            </span>
          </div>

          <div className={styles.filterGroups}>
            <EmployedsMobileFilterGroup
              label='Гражданство'
              options={transformNationalities()}
              selectedValues={nationalityKeywords}
              onItemClick={(optionId) => handleItemClick('nationalities', optionId)}
              onItemRemove={(optionId) => handleItemRemove('nationalities', optionId)}
              onShowAll={() => openFilterDrawer('nationalities')}
              count={4}
            />

            <EmployedsMobileFilterGroup
              label='Категории исполнителей'
              options={transformCategories()}
              selectedValues={categoriesKeywords}
              onItemClick={(optionId) => handleItemClick('categories', optionId)}
              onItemRemove={(optionId) => handleItemRemove('categories', optionId)}
              onShowAll={() => openFilterDrawer('categories')}
              count={4}
            />

            <div>
              <div className={styles.label}>
                <span>Пол</span>
              </div>
              <div className={styles.genderCheckboxRow}>
                {transformGenders().map((option) => (
                  <div key={option.id} className={styles.genderCheckboxOption}>
                    <EmployedsFilterCheckbox
                      checked={genderKeywords.includes(option.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setGenderKeywords([option.id]);
                        } else {
                          setGenderKeywords([]);
                        }
                      }}
                      label={option.label}
                    />
                  </div>
                ))}
              </div>
            </div>

            {!showFilters && (
              <EmployedsMobileFilterGroup
                label='Проблема'
                options={transformIssues()}
                selectedValues={issueKeywords}
                onItemClick={(optionId) => handleItemClick('issues', optionId)}
                onItemRemove={(optionId) => handleItemRemove('issues', optionId)}
                onShowAll={() => openFilterDrawer('issues')}
              />
            )}

            <div>
              <div className={styles.label}>
                <span>Наличие договора</span>
              </div>
              <div className={styles.genderCheckboxRow}>
                {transformContracts().map((option) => (
                  <div key={option.id} className={styles.genderCheckboxOption}>
                    <EmployedsFilterCheckbox
                      checked={contractKeywords.includes(option.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setContractKeywords([option.id]);
                        } else {
                          setContractKeywords([]);
                        }
                      }}
                      label={option.label}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className={styles.label}>
                <span>Дата добавления</span>
              </div>
              <div className={styles.dateButton} onClick={openDatePicker}>
                <span
                  className={classNames(
                    styles.dateButtonText,
                    !dateAddStart && !dateAddEnd && styles.dateButtonTextPlaceholder,
                  )}>
                  {formatDateRangeForDisplay(dateAddStart, dateAddEnd) || 'Выбери период'}
                </span>
                <IconCalendar />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <FilterButton variant='apply' onClick={handleApplyFilters}>
            Применить
          </FilterButton>
        </div>
      </Drawer>

      <EmployedsMobileFilterDrawer
        opened={filterDrawerState.opened}
        onClose={() => setFilterDrawerState((prev) => ({ ...prev, opened: false }))}
        title={filterDrawerState.title}
        options={
          filterDrawerState.filterType === 'categories'
            ? transformCategories()
            : filterDrawerState.options
        }
        selectedValues={filterDrawerState.selectedValues}
        onChange={handleFilterDrawerChange}
        onApply={handleFilterDrawerApply}
        onReset={handleFilterDrawerReset}
        showSearch={
          filterDrawerState.filterType === 'categories'
            ? true
            : filterDrawerState.options.length > 6
        }
        categoriesServer={filterDrawerState.filterType === 'categories'}
        onCategoriesSearch={
          filterDrawerState.filterType === 'categories'
            ? handleCategorysServerSearch
            : undefined
        }
        categoriesHasNext={
          filterDrawerState.filterType === 'categories' && hasNextCategoryPage
        }
        onCategoriesLoadMore={
          filterDrawerState.filterType === 'categories' ? loadMoreCategories : undefined
        }
        categoriesFetching={
          filterDrawerState.filterType === 'categories' && isFetchingCategorys
        }
        categoriesLoading={
          filterDrawerState.filterType === 'categories' && isLoadingCategorys
        }
      />

      <Drawer
        opened={openedDate}
        onClose={closeDate}
        styles={{
          content: {
            borderRadius: '13px 13px 0px 0px',
            backgroundColor: '#FFF',
            padding: '12px 16px 32px 16px',
            boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          },
          body: {
            padding: '0',
          },
        }}
        position='bottom'
        size={520}
        zIndex={10000}
        withCloseButton={false}>
        <Box>
          <div className={styles.datePickerHeader}>
            <span className={styles.datePickerTitle}>Дата добавления</span>
            <span
              className={styles.datePickerReset}
              onClick={() => {
                setRangeValue([null, null]);
                setDateAddStart(null);
                setDateAddEnd(null);
                closeDate();
              }}>
              Сбросить
            </span>
          </div>
          <Center>
            <DatePicker
              size='lg'
              type='range'
              value={rangeValue}
              onChange={(value) => {
                const newValue = value as [Date | null, Date | null];
                setRangeValue(newValue);
                if (newValue[0] != null) setDateAddStart(newValue[0]);
                if (newValue[1] != null) setDateAddEnd(newValue[1]);
                if (newValue[0] != null && newValue[1] != null) {
                  closeDate();
                }
              }}
              highlightToday
              allowSingleDateInRange
            />
          </Center>
        </Box>
      </Drawer>
    </>
  );
};
